import {useGraphql} from "@/core/graphql/service";
import {useTime} from "@/core/store/time";
import deepToRaw from "@/core/util/vue/deepToRaw";
import {DeliveryCalculate, OrderProfile, OrderProfileAttributesValue} from "@/gql/gen";
import saleProfileCalcDeliveryMutation, {
    MutationSaleProfileCalcDeliveryVars
} from "@/gql/gen/mutation/saleProfileCalcDeliveryMutation";
import {GeoCoordinates} from "@/modules/geo/class/GeoCoordinates";
import {getOrderAttrsAddress, OrderProfileModel} from "@/modules/shop/composable/util";
import {getOrderProfileDeliveryLocationHash} from "@/modules/shop/util/getOrderProfileDeliveryLocationHash";
import cloneDeep from "lodash-es/cloneDeep";
import {storeToRefs} from "pinia";
import {computed, reactive, ref} from "vue";

export function useProfileModel(sourceData: OrderProfile): OrderProfileModel {

    const {useMutation, responseSelection} = useGraphql()

    const timers = useTime()

    const {timestampUnixIntervalMinute} = storeToRefs(timers)

    const source = reactive<OrderProfile>({...sourceData})
    const deliveryCalculateResult = ref<DeliveryCalculate>()

    const id = computed(() => source.ID)
    const name = computed(() => address.value)
    const caption = computed(() => {
        const parts = [
            source.ATTR.PRIVATE_HOUSE && 'Частный дом' || source.ATTR.FLAT && `Квартира: ${source.ATTR.FLAT}`,
            source.ATTR.FLOOR && `Этаж: ${source.ATTR.FLOOR}`,
            source.ATTR.LIFT && 'Лифт',
        ]
        return parts.filter(Boolean).join(', ')
    })

    const address = computed(() => getOrderAttrsAddress(source.ATTR))
    const coords = computed(() => source.ATTR.HOUSE_COORDS ? new GeoCoordinates(source.ATTR.HOUSE_COORDS) : null)
    const navCoords = computed(() => {
        const v = [source.ATTR.STREET_COORDS, source.ATTR.HOUSE_COORDS].find(Boolean)
        return v ? GeoCoordinates.toObject(v) : null
    })

    const setAttrs = (attrs: Partial<OrderProfileAttributesValue>) => {
        Object.assign(source.ATTR, attrs)
    }

    const addressHash = computed<string | null>(() => {
        return getOrderProfileDeliveryLocationHash(source)
    })

    const deliveryTimeMinutes = computed(() => {
        return deliveryCalculateResult.value && deliveryCalculateResult.value.RES_TIME ?
            Math.round((deliveryCalculateResult.value.RES_TIME - timestampUnixIntervalMinute.value) / 60) : null
    })

    const beforeSave = () => {
        if (source.ATTR.PRIVATE_HOUSE) {
            source.ATTR.FLAT = null
            source.ATTR.LIFT = null
            source.ATTR.FLOOR = null
        }
    }

    const getRawAttrValues = () => {
        return deepToRaw(source.ATTR)
    }

    const textInputFields = computed(() => {
        return {
            address: source.ATTR.ADDRESS,
            street: source.ATTR.STREET,
            city: source.ATTR.CITY,
            house: source.ATTR.HOUSE,
        }
    })

    const deliveryCalculateNearest = useMutation(saleProfileCalcDeliveryMutation({
        payload: {
            __fragment: 'DeliveryCalculateFields'
        },
        ...responseSelection()
    }), {
        variables: computed<MutationSaleProfileCalcDeliveryVars>(() => {
            return {
                profileId: source.ID || 0,
                attrs: getRawAttrValues(),
                timeMode: 'nearest'
            }
        }),
        onResult: ({success, payload}) => {
        }
    })

    const updateSource = (data: OrderProfile) => {
        Object.assign(source, data)
    }

    const clone = () => {
        const model = useProfileModel(cloneDeep(deepToRaw(source)))
        model.deliveryCalculateResult.value = deliveryCalculateResult.value
        return model
    }

    const getId = () => {
        return source.ID
    }

    return {
        id,
        addressHash,
        name,
        caption,
        source,
        address,
        coords,
        setAttrs,
        beforeSave,
        getRawAttrValues,
        deliveryTimeMinutes,
        textInputFields,
        navCoords,
        deliveryCalculateNearest,
        deliveryCalculateResult,
        updateSource,
        clone,
        getId,
    }
}
